import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as CM from '@radix-ui/react-context-menu';
import * as PO from '@radix-ui/react-popover';
import { ArrowDown, ArrowUp, Check, Columns3, FileSpreadsheet, Filter, Group, Pencil, Plus, RefreshCw, Save, Search, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { ColumnMeta, ListQuery, ListResult } from '@/lib/types';
import { delphiColor, formatValue, isBoolType, isDateType, isNumericType } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { GridColumn, GridProps } from './types';
import { parseFilter } from './filters';
import { CellEditor } from './CellEditor';
import { useGridLayout } from './layout';
import { confirmDialog } from '@/ui/confirm';
import { onDataChanged } from './events';

const BLOCK = 200;
const ROW_H = 26;

type Item = { type: 'row'; row: Record<string, any>; index: number } | { type: 'group'; key: string; label: string; level: number; count: number; sums: Record<string, number> };

/** Meta veriden varsayılan kolon üretimi (Delphi cxGrid CreateAllItems + DisplayFormat) */
export function columnsFromMeta(meta: ColumnMeta[]): GridColumn[] {
  return meta.map((m) => ({
    key: m.name,
    caption: m.name,
    sqlType: m.sqlType,
    align: isNumericType(m.sqlType) ? 'right' : isBoolType(m.sqlType) ? 'center' : undefined,
    width: defaultWidth(m),
    editor: isBoolType(m.sqlType) ? 'check' : isNumericType(m.sqlType) ? 'number' : isDateType(m.sqlType) ? 'date' : m.sqlType === 'text' || m.sqlType === 'ntext' ? 'memo' : 'text',
    maxLength: m.length && m.length > 0 && m.length < 8000 ? (m.sqlType.startsWith('n') ? m.length / 2 : m.length) : undefined,
    readonly: m.identity || m.readOnly,
  }));
}

function defaultWidth(m: ColumnMeta) {
  if (isBoolType(m.sqlType)) return 56;
  if (isDateType(m.sqlType)) return 110;
  if (isNumericType(m.sqlType)) return 100;
  const len = m.length && m.length > 0 ? (m.sqlType.startsWith('n') ? m.length / 2 : m.length) : 60;
  return Math.max(70, Math.min(320, len * 7.5));
}

export function DataGrid(props: GridProps) {
  const { fetch, editing } = props;
  const [meta, setMeta] = useState<ColumnMeta[] | null>(null);
  const [sort, setSort] = useState<{ field: string; desc?: boolean }[]>(props.defaultSort ?? []);
  const [filterText, setFilterText] = useState<Record<string, string>>({});
  const [appliedFilter, setAppliedFilter] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // hızlı arama: yazmayı bırakınca (500 ms) uygulanır; Enter/blur hemen uygular
  useEffect(() => {
    const t = window.setTimeout(() => setAppliedSearch(search), 500);
    return () => window.clearTimeout(t);
  }, [search]);
  const [showFilter, setShowFilter] = useState(!!props.filterRow);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [reload, setReload] = useState(0);
  const [blocks, setBlocks] = useState<Map<number, Record<string, any>[]>>(new Map());
  const [allRows, setAllRows] = useState<Record<string, any>[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [totals, setTotals] = useState<Record<string, number | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [edit, setEdit] = useState<{ index: number | 'new'; draft: Record<string, any>; original: Record<string, any> | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef(new Set<number>());
  const genRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const baseColumns = useMemo<GridColumn[]>(() => {
    if (props.columns && !props.buildColumns) return props.columns;
    if (!meta) return props.columns ?? [];
    return props.buildColumns ? props.buildColumns(meta) : columnsFromMeta(meta);
  }, [props.columns, props.buildColumns, meta]);

  const layout = useGridLayout(props.layoutKey, baseColumns);
  const columns = layout.columns;
  const visible = useMemo(() => columns.filter((c) => !c.hidden), [columns]);

  const query = useMemo<ListQuery>(() => {
    const filters = Object.entries(appliedFilter).flatMap(([k, v]) => parseFilter(k, v, columns.find((c) => c.key === k)?.sqlType));
    const totalsReq = columns.filter((c) => c.total).map((c) => ({ field: c.key, kind: c.total! }));
    return { sort, filters, search: appliedSearch || undefined, totals: totalsReq };
  }, [sort, appliedFilter, appliedSearch, columns]);

  const resetKey = JSON.stringify([props.queryKey, query, groupBy.length > 0, reload]);

  const loadBlock = useCallback(async (b: number, gen: number, refresh = false) => {
    if (pending.current.has(b)) return;
    pending.current.add(b);
    setLoading(true);
    try {
      const r: ListResult = await fetch({ ...query, offset: b * BLOCK, limit: BLOCK, refresh });
      if (gen !== genRef.current) return;
      setMeta((m) => (m && sameMeta(m, r.columns) ? m : r.columns));
      setTotal(r.total);
      if (b === 0) setTotals(r.totals ?? {});
      setBlocks((prev) => new Map(prev).set(b, r.rows));
      setError(null);
    } catch (e: any) {
      if (gen === genRef.current) setError(e.message);
    } finally {
      pending.current.delete(b);
      if (gen === genRef.current) setLoading(false);
    }
  }, [fetch, query]);

  // sorgu değişince sıfırla
  useEffect(() => {
    const gen = ++genRef.current;
    pending.current.clear();
    setBlocks(new Map());
    setAllRows(null);
    setTotal(null);
    setSel(null);
    if (groupBy.length) {
      setLoading(true);
      fetch({ ...query, all: true, refresh: reload > 0 }).then((r) => {
        if (gen !== genRef.current) return;
        setMeta(r.columns);
        setAllRows(r.rows);
        setTotal(r.total);
        setTotals(r.totals ?? {});
        setError(null);
      }).catch((e) => gen === genRef.current && setError(e.message)).finally(() => gen === genRef.current && setLoading(false));
    } else {
      loadBlock(0, gen, reload > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const rowAt = useCallback((i: number) => (allRows ? allRows[i] : blocks.get(Math.floor(i / BLOCK))?.[i % BLOCK]), [allRows, blocks]);

  // gruplama
  const items = useMemo<Item[] | null>(() => {
    if (!groupBy.length || !allRows) return null;
    const out: Item[] = [];
    const sumCols = columns.filter((c) => c.total === 'sum').map((c) => c.key);
    const walk = (rows: { r: Record<string, any>; i: number }[], level: number, prefix: string) => {
      const key = groupBy[level];
      const col = columns.find((c) => c.key === key);
      const groups = new Map<string, { r: Record<string, any>; i: number }[]>();
      for (const x of rows) {
        const g = formatValue(x.r[key], col?.sqlType, col?.mask) || '(boş)';
        (groups.get(g) ?? groups.set(g, []).get(g)!).push(x);
      }
      for (const [g, list] of [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'tr', { numeric: true }))) {
        const gk = `${prefix}/${g}`;
        const sums: Record<string, number> = {};
        for (const s of sumCols) sums[s] = list.reduce((a, x) => a + (Number(x.r[s]) || 0), 0);
        out.push({ type: 'group', key: gk, label: `${col?.caption ?? key}: ${g}`, level, count: list.length, sums });
        if (collapsed.has(gk)) continue;
        if (level + 1 < groupBy.length) walk(list, level + 1, gk);
        else for (const x of list) out.push({ type: 'row', row: x.r, index: x.i });
      }
    };
    walk(allRows.map((r, i) => ({ r, i })), 0, '');
    return out;
  }, [groupBy, allRows, columns, collapsed]);

  const count = items ? items.length : total ?? 0;
  const virt = useVirtualizer({ count, getScrollElement: () => scrollRef.current, estimateSize: () => ROW_H, overscan: 20 });
  const vItems = virt.getVirtualItems();

  // görünen bloklar yüklü değilse getir
  useEffect(() => {
    if (items || total == null) return;
    const need = new Set<number>();
    for (const v of vItems) if (!blocks.has(Math.floor(v.index / BLOCK))) need.add(Math.floor(v.index / BLOCK));
    for (const b of need) loadBlock(b, genRef.current);
  }, [vItems, blocks, items, total, loadBlock]);

  const selectedRow = sel != null ? rowAt(sel) ?? null : null;
  useEffect(() => { props.onSelectionChange?.(selectedRow ?? null); }, [selectedRow]);

  const refresh = () => setReload((r) => r + 1);
  // başka sekmede (kayıt formu) aynı ekranın verisi değişince yenile
  useEffect(() => onDataChanged((tag) => { if (props.queryKey.includes(tag)) refresh(); }), [props.queryKey]);

  // ---------------------------------------------------------------- düzenleme
  const startNew = async () => {
    if (!editing?.canInsert || edit) return;
    let draft: Record<string, any> = {};
    try {
      if (editing.onNewDefaults) draft = { ...(await editing.onNewDefaults(draft)) };
    } catch (e: any) {
      toast.error(e.message);
    }
    setEdit({ index: 'new', draft, original: null });
  };
  const startEdit = () => {
    if (!editing?.canUpdate || edit || sel == null || !selectedRow) return;
    setEdit({ index: sel, draft: { ...selectedRow }, original: selectedRow });
  };
  const cancelEdit = () => setEdit(null);
  const saveEdit = async () => {
    if (!edit || !editing) return;
    setSaving(true);
    try {
      await editing.onSave(edit.draft, edit.original);
      toast.success('Kaydedildi');
      setEdit(null);
      refresh();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!editing?.canDelete || !selectedRow || edit) return;
    if (!(await confirmDialog('Silmek İstediğinizden Eminmisiniz ?', { title: 'Kayıt Sil', ok: 'Sil', danger: true }))) return;
    try {
      await editing.onDelete(selectedRow);
      toast.success('Silindi');
      refresh();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Delphi: F2 yeni, F5 düzelt/kaydet, F3 sil, ESC vazgeç
    if (e.key === 'F2') { e.preventDefault(); startNew(); }
    else if (e.key === 'F5') { e.preventDefault(); edit ? saveEdit() : startEdit(); }
    else if (e.key === 'F3') { e.preventDefault(); remove(); }
    else if (e.key === 'Escape' && edit) { e.preventDefault(); cancelEdit(); }
    else if (e.key === 'Enter' && edit && !(e.target as HTMLElement).closest('[data-noenter]')) { e.preventDefault(); setTimeout(saveEdit, 0); }
    else if (!edit && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const n = Math.max(0, Math.min(count - 1, (sel ?? -1) + (e.key === 'ArrowDown' ? 1 : -1)));
      setSel(n);
      virt.scrollToIndex(n, { align: 'auto' });
    } else if (!edit && e.key === 'Enter' && selectedRow && props.onRowOpen) {
      e.preventDefault();
      props.onRowOpen(selectedRow);
    }
  };

  const toggleSort = (key: string, add: boolean) => {
    setSort((s) => {
      const cur = s.find((x) => x.field === key);
      const next = cur ? (cur.desc ? null : { field: key, desc: true }) : { field: key, desc: false };
      const rest = add ? s.filter((x) => x.field !== key) : [];
      return next ? [...rest, next] : rest;
    });
  };

  const applyFilters = () => {
    setAppliedFilter(Object.fromEntries(Object.entries(filterText).filter(([, v]) => v.trim())));
  };

  const totalWidth = visible.reduce((a, c) => a + (c.width ?? 120), 0) + 36;
  const hasTotals = visible.some((c) => c.total);

  const cellStyle = (c: GridColumn, row?: Record<string, any>) => {
    const st: React.CSSProperties = { width: c.width ?? 120, minWidth: c.width ?? 120, textAlign: c.align };
    if (row && c.colors) {
      const condOk = !c.colors.cond || c.colors.condValue == null || compare(Number(row[c.key]), c.colors.cond, c.colors.condValue);
      if (condOk) {
        if (c.colors.font) st.color = delphiColor(c.colors.font);
        if (c.colors.back) st.background = delphiColor(c.colors.back);
      }
    }
    return st;
  };

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col', props.bare ? '' : 'panel rounded')} style={{ height: props.height ?? '100%' }} onKeyDown={onKeyDown}>
      {!props.bare && (
        <div className="flex flex-wrap items-center gap-1.5 border-b b-border px-2 py-1.5">
          {props.toolbarLeft}
          {editing && (
            <>
              <button className="btn" disabled={!editing.canInsert || !!edit} onClick={startNew} title="Yeni (F2)"><Plus size={14} /> Yeni</button>
              <button className="btn" disabled={!editing.canUpdate || !!edit || sel == null} onClick={startEdit} title="Düzelt (F5)"><Pencil size={14} /> Düzelt</button>
              <button className="btn btn-danger" disabled={!editing.canDelete || !!edit || sel == null} onClick={remove} title="Sil (F3)"><Trash2 size={14} /> Sil</button>
              {edit && <>
                <button className="btn btn-primary" disabled={saving} onClick={saveEdit} title="Kaydet (F5 / Enter)"><Save size={14} /> Kaydet</button>
                <button className="btn" onClick={cancelEdit} title="Vazgeç (Esc)"><Undo2 size={14} /> Vazgeç</button>
              </>}
              <span className="mx-1 h-5 w-px bg-[var(--border)]" />
            </>
          )}
          <div className="relative">
            <Search size={13} className="absolute left-2 top-[7px] muted" />
            <input className="inp !h-7 w-48 pl-7" placeholder="Ara…" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setAppliedSearch(search); }} onBlur={() => setAppliedSearch(search)} />
          </div>
          <button className={cn('btn', showFilter && 'bg-[var(--grid-header)]')} onClick={() => setShowFilter((s) => !s)} title="Filtre satırı"><Filter size={14} /></button>
          {props.groupBox !== false && (
            <ColumnPicker label={<><Group size={14} /> Grupla</>} columns={columns} selected={groupBy} onToggle={(k) => setGroupBy((g) => (g.includes(k) ? g.filter((x) => x !== k) : [...g, k]))} />
          )}
          <ColumnPicker label={<Columns3 size={14} />} title="Kolonlar" columns={columns} selected={columns.filter((c) => !c.hidden).map((c) => c.key)} onToggle={(k) => layout.toggleHidden(k)} />
          <button className="btn" onClick={refresh} title="Yenile"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
          {props.onExport && <button className="btn" onClick={() => props.onExport!({ ...query, all: true }, visible)} title="Excel'e aktar"><FileSpreadsheet size={14} /> Excel</button>}
          <div className="ml-auto flex items-center gap-2">
            {props.toolbarRight}
            <span className="text-xs muted">{total == null ? '' : `${total.toLocaleString('tr-TR')} kayıt`}</span>
          </div>
        </div>
      )}
      {groupBy.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 border-b b-border px-2 py-1 text-xs">
          <span className="muted">Gruplama:</span>
          {groupBy.map((g) => (
            <span key={g} className="flex items-center gap-1 rounded bg-brand-100 px-2 py-0.5 text-brand-900">
              {columns.find((c) => c.key === g)?.caption ?? g}
              <button onClick={() => setGroupBy((x) => x.filter((y) => y !== g))}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      {error && <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-red-700">{error}</div>}
      <div ref={scrollRef} tabIndex={0} className="relative min-h-0 flex-1 overflow-auto outline-none scroll-thin">
        <div style={{ width: totalWidth, minWidth: '100%' }}>
          {/* başlık */}
          <div className="sticky top-0 z-10 flex border-b b-border bg-[var(--grid-header)] text-[12px] font-medium" style={{ height: 28 }}>
            <div className="w-9 shrink-0" />
            {visible.map((c) => {
              const s = sort.find((x) => x.field === c.key);
              return (
                <CM.Root key={c.key}>
                  <CM.Trigger asChild>
                    <div className="group relative flex shrink-0 cursor-pointer select-none items-center gap-1 border-r b-border px-1.5" style={cellStyle(c)}
                      onClick={(e) => toggleSort(c.key, e.shiftKey)} title={c.field?.description ?? c.caption}>
                      <span className={cn('flex-1 truncate', c.align === 'right' && 'text-right')}>{c.caption}</span>
                      {s && (s.desc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
                      <span className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-brand-500/40"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => startResize(e, c, layout.setWidth)} />
                    </div>
                  </CM.Trigger>
                  <CM.Portal>
                    <CM.Content className="panel z-50 min-w-44 rounded p-1 text-[12.5px] shadow-lg">
                      <CM.Item className="cursor-pointer rounded px-2 py-1 outline-none hover:bg-[var(--grid-header)]" onSelect={() => setSort([{ field: c.key }])}>Artan sırala</CM.Item>
                      <CM.Item className="cursor-pointer rounded px-2 py-1 outline-none hover:bg-[var(--grid-header)]" onSelect={() => setSort([{ field: c.key, desc: true }])}>Azalan sırala</CM.Item>
                      <CM.Item className="cursor-pointer rounded px-2 py-1 outline-none hover:bg-[var(--grid-header)]" onSelect={() => setGroupBy((g) => (g.includes(c.key) ? g : [...g, c.key]))}>Bu kolona göre grupla</CM.Item>
                      <CM.Item className="cursor-pointer rounded px-2 py-1 outline-none hover:bg-[var(--grid-header)]" onSelect={() => layout.toggleHidden(c.key)}>Kolonu gizle</CM.Item>
                      <CM.Separator className="my-1 h-px bg-[var(--border)]" />
                      <CM.Item className="cursor-pointer rounded px-2 py-1 outline-none hover:bg-[var(--grid-header)]" onSelect={() => layout.reset()}>Düzeni sıfırla</CM.Item>
                    </CM.Content>
                  </CM.Portal>
                </CM.Root>
              );
            })}
          </div>
          {/* filtre satırı */}
          {showFilter && (
            <div className="sticky top-[28px] z-10 flex border-b b-border bg-[var(--panel)]" style={{ height: 26 }}>
              <div className="flex w-9 shrink-0 items-center justify-center muted"><Filter size={12} /></div>
              {visible.map((c) => (
                <div key={c.key} className="shrink-0 border-r b-border" style={{ width: c.width ?? 120 }}>
                  {c.filterable === false ? null : (
                    <input className="h-full w-full bg-transparent px-1.5 text-[12px] outline-none focus:bg-brand-50 dark:focus:bg-brand-900/30"
                      placeholder={isNumericType(c.sqlType) ? '>0, 1..9' : isDateType(c.sqlType) ? 'gg.aa.yyyy' : ''}
                      value={filterText[c.key] ?? ''} onChange={(e) => setFilterText((f) => ({ ...f, [c.key]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }} onBlur={applyFilters} />
                  )}
                </div>
              ))}
            </div>
          )}
          {/* yeni satır */}
          {edit?.index === 'new' && (
            <div className="flex border-b-2 border-brand-500 bg-brand-50/60 dark:bg-brand-900/30" style={{ height: ROW_H + 2 }}>
              <div className="flex w-9 shrink-0 items-center justify-center text-brand-700"><Plus size={13} /></div>
              {visible.map((c, i) => (
                <div key={c.key} className="shrink-0 border-r b-border" style={{ width: c.width ?? 120 }}>
                  <CellEditor col={c} row={edit.draft} autoFocus={i === firstEditable(visible)} onChange={(p) => setEdit((e) => e && { ...e, draft: { ...e.draft, ...p } })} />
                </div>
              ))}
            </div>
          )}
          {/* satırlar */}
          <div style={{ height: virt.getTotalSize(), position: 'relative' }}>
            {vItems.map((v) => {
              const it: Item | undefined = items ? items[v.index] : undefined;
              if (it?.type === 'group') {
                return (
                  <div key={v.key} className="absolute left-0 flex w-full cursor-pointer items-center gap-2 border-b b-border bg-[var(--grid-header)] text-[12px] font-medium"
                    style={{ top: v.start, height: ROW_H, paddingLeft: 8 + it.level * 16 }}
                    onClick={() => setCollapsed((s) => { const n = new Set(s); n.has(it.key) ? n.delete(it.key) : n.add(it.key); return n; })}>
                    <span>{collapsed.has(it.key) ? '▸' : '▾'}</span> {it.label} <span className="muted">({it.count})</span>
                    {Object.entries(it.sums).map(([k, s]) => <span key={k} className="muted">· {columns.find((c) => c.key === k)?.caption}: {formatValue(s, 'float', columns.find((c) => c.key === k)?.mask ?? '#,#0.00')}</span>)}
                  </div>
                );
              }
              const index = it?.type === 'row' ? it.index : v.index;
              const row = it?.type === 'row' ? it.row : rowAt(index);
              const editingThis = edit && edit.index === index;
              const r = editingThis ? edit!.draft : row;
              return (
                <div key={v.key} data-row-index={index} data-loaded={row ? '1' : undefined}
                  className={cn('absolute left-0 flex border-b b-border text-[12.5px]',
                    index === sel ? 'bg-[var(--grid-select)]' : index % 2 ? 'bg-[var(--grid-row-alt)]' : '',
                    editingThis && 'outline outline-2 outline-brand-500 z-[1]')}
                  style={{ top: v.start, height: ROW_H, width: totalWidth, minWidth: '100%' }}
                  onClick={() => !edit && setSel(index)}
                  onDoubleClick={() => { if (!row || edit) return; if (props.onRowOpen) props.onRowOpen(row); else if (editing?.canUpdate) { setSel(index); setEdit({ index, draft: { ...row }, original: row }); } }}>
                  <div className="flex w-9 shrink-0 items-center justify-end pr-1.5 text-[10.5px] muted">{editingThis ? <Pencil size={11} className="text-brand-700" /> : index + 1}</div>
                  {!r ? <div className="px-2 muted">…</div> : visible.map((c, i) => (
                    <div key={c.key} className="flex shrink-0 items-center overflow-hidden border-r b-border" style={cellStyle(c, r)}>
                      {editingThis ? (
                        <CellEditor col={c} row={r} autoFocus={i === firstEditable(visible)} onChange={(p) => setEdit((e) => e && { ...e, draft: { ...e.draft, ...p } })} />
                      ) : (
                        <span className={cn('w-full truncate px-1.5', c.align === 'right' && 'text-right', c.align === 'center' && 'text-center')}>
                          {isBoolType(c.sqlType) || c.editor === 'check' ? (r[c.key] ? <Check size={13} className="inline" /> : '') : c.display ? c.display(r) : formatValue(r[c.key], c.sqlType, c.mask)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {count === 0 && !loading && edit?.index !== 'new' && <div className="p-6 text-center muted">{props.emptyText ?? 'Kayıt yok'}</div>}
          {/* alt toplamlar */}
          {hasTotals && (
            <div className="sticky bottom-0 z-10 flex border-t-2 b-border bg-[var(--grid-header)] text-[12px] font-semibold" style={{ height: 26 }}>
              <div className="w-9 shrink-0" />
              {visible.map((c) => (
                <div key={c.key} className="flex shrink-0 items-center justify-end border-r b-border px-1.5" style={{ width: c.width ?? 120 }}>
                  {c.total ? formatValue(totals[`${c.total}:${c.key}`] ?? null, c.total === 'count' ? 'int' : 'float', c.total === 'count' ? null : c.mask ?? '#,#0.00') : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function firstEditable(cols: GridColumn[]) {
  return cols.findIndex((c) => !c.readonly && !c.noFocus && c.editor !== 'readonly');
}

function sameMeta(a: ColumnMeta[], b: ColumnMeta[]) {
  return a.length === b.length && a.every((x, i) => x.name === b[i].name && x.sqlType === b[i].sqlType);
}

function compare(v: number, op: string, ref: number) {
  switch (op.trim()) {
    case '>': return v > ref;
    case '>=': return v >= ref;
    case '<': return v < ref;
    case '<=': return v <= ref;
    case '<>': case '!=': return v !== ref;
    default: return v === ref;
  }
}

function startResize(e: React.PointerEvent, c: GridColumn, setWidth: (k: string, w: number) => void) {
  e.preventDefault();
  e.stopPropagation();
  const startX = e.clientX;
  const start = c.width ?? 120;
  const move = (ev: PointerEvent) => setWidth(c.key, Math.max(40, start + ev.clientX - startX));
  const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

function ColumnPicker({ label, title, columns, selected, onToggle }: { label: React.ReactNode; title?: string; columns: GridColumn[]; selected: string[]; onToggle: (k: string) => void }) {
  return (
    <PO.Root>
      <PO.Trigger className="btn" title={title}>{label}</PO.Trigger>
      <PO.Portal>
        <PO.Content align="start" className="panel z-50 max-h-80 w-60 overflow-auto rounded p-1 shadow-lg scroll-thin">
          {columns.map((c) => (
            <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[12.5px] hover:bg-[var(--grid-header)]">
              <input type="checkbox" checked={selected.includes(c.key)} onChange={() => onToggle(c.key)} />
              <span className="truncate">{c.caption}</span>
            </label>
          ))}
        </PO.Content>
      </PO.Portal>
    </PO.Root>
  );
}
